import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link, useSearchParams } from 'react-router-dom';
import { CalendarDaysIcon } from 'lucide-react';
import { DataTable, EmptyState, ErrorNotice, Panel, Pill, SearchInput, SegmentedControl, SkeletonTable, type PillTone } from '@bcecloud/portal-ui';
import { errorMessage, getJson } from '@/lib/api';
import { sortRows } from '@/lib/facet-table';
import { useTableSort } from '@/lib/use-table-sort';

/**
 * The client's events, upcoming and past, with where each one stands. An
 * event is a request that came through the inquiry stage and was approved,
 * or one BCE delivered against the client's Salesforce accounts; what is
 * still being asked for lives on the Inquiries tab.
 */

/** A workspace header as the portal API shapes it (the client's own list). */
export interface OrgWorkspace {
  readonly id: string;
  readonly source: 'BOOKING' | 'EVENT';
  readonly reference: string | null;
  readonly programName: string | null;
  readonly status: string | null;
  readonly startAt: string | null;
  readonly city: string | null;
  readonly state: string | null;
  readonly venueName: string | null;
  readonly estimatedParticipants: number | null;
  readonly finalParticipants: number | null;
  readonly eventId: string | null;
  readonly bookingRequestId: string | null;
}

/** Booking statuses that are still an inquiry: not yet approved, so not yet an event. */
const INQUIRY_STATUSES: ReadonlySet<string> = new Set([
  'DRAFT',
  'SUBMITTED',
  'AWAITING_TEAM_APPROVAL',
  'AWAITING_ORG_APPROVAL',
  'UNDER_BCE_REVIEW',
  'DATE_HELD',
  'INFORMATION_REQUIRED',
]);

/** How a booking's stage reads to staff, and its tone. */
const STAGE_LABELS: Readonly<Record<string, { readonly label: string; readonly tone: PillTone }>> = {
  APPROVED: { label: 'Approved', tone: 'brand' },
  CONFIRMED: { label: 'Confirmed', tone: 'success' },
  MATERIALS_IN_PREPARATION: { label: 'Materials in preparation', tone: 'info' },
  MATERIALS_SHIPPED: { label: 'Materials shipped', tone: 'info' },
  READY_FOR_DELIVERY: { label: 'Ready for delivery', tone: 'success' },
  COMPLETED: { label: 'Completed', tone: 'neutral' },
  FOLLOW_UP: { label: 'Follow-up', tone: 'neutral' },
  CANCELLED: { label: 'Canceled', tone: 'neutral' },
  REBOOKING: { label: 'Rebooking', tone: 'warning' },
};

const SORT_FIELDS = {
  event: 'programName',
  date: 'startAt',
  location: 'city',
  participants: 'participants',
  stage: 'stageLabel',
} as const;
const SORT_KEYS = Object.values(SORT_FIELDS);

const SHOW = { upcoming: 'upcoming', past: 'past' } as const;
type Show = (typeof SHOW)[keyof typeof SHOW];

function shortDate(iso: string | null): string {
  if (!iso) return 'No date';
  return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' });
}

function stageOf(workspace: OrgWorkspace): { readonly label: string; readonly tone: PillTone } {
  if (workspace.source === 'EVENT') return { label: workspace.status ?? 'Delivered', tone: 'success' };
  return STAGE_LABELS[workspace.status ?? ''] ?? { label: workspace.status ?? 'Event', tone: 'neutral' };
}

export function OrgEventsPanel({ organizationId, organizationName }: { readonly organizationId: string; readonly organizationName: string }): React.ReactElement {
  const [params, setParams] = useSearchParams();
  const show: Show = params.get('show') === SHOW.past ? SHOW.past : SHOW.upcoming;
  const { sort, onSort } = useTableSort(SORT_KEYS, { key: SORT_FIELDS.date, direction: show === SHOW.past ? 'desc' : 'asc' });
  const [search, setSearch] = useState('');
  const needle = search.trim().toLowerCase();

  const workspaces = useQuery({
    queryKey: ['org-workspaces', organizationId],
    queryFn: ({ signal }) => getJson<OrgWorkspace[]>(`/organizations/${organizationId}/workspaces`, undefined, signal),
  });

  const todayStart = new Date();
  todayStart.setUTCHours(0, 0, 0, 0);
  const events = (workspaces.data ?? []).filter((workspace) => workspace.source === 'EVENT' || !INQUIRY_STATUSES.has(workspace.status ?? ''));
  const isPast = (workspace: OrgWorkspace): boolean => workspace.startAt !== null && new Date(workspace.startAt) < todayStart;
  const rows = sortRows(
    events
      .filter((workspace) => (show === SHOW.past ? isPast(workspace) : !isPast(workspace)))
      .filter(
        (workspace) =>
          needle.length === 0 ||
          [workspace.programName, workspace.reference, workspace.city, workspace.venueName, stageOf(workspace).label].some((field) => (field ?? '').toLowerCase().includes(needle)),
      )
      .map((workspace) => {
        const stage = stageOf(workspace);
        return { ...workspace, participants: workspace.finalParticipants ?? workspace.estimatedParticipants, stageLabel: stage.label, stageTone: stage.tone };
      }),
    sort,
  );
  const upcomingCount = events.filter((workspace) => !isPast(workspace)).length;
  const pastCount = events.length - upcomingCount;

  return (
    <div className="mt-6 space-y-4">
      <div>
        <h2 className="text-portal-section font-bold tracking-[-0.4px]">Events</h2>
        <p className="mt-1 text-portal-small text-portal-muted">
          {organizationName}'s events, once a request has come through the inquiry stage, plus everything BCE has delivered for them.
        </p>
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3">
        <SegmentedControl
          label="Which events to show"
          options={[
            { key: SHOW.upcoming, label: `Upcoming (${upcomingCount})` },
            { key: SHOW.past, label: `Past (${pastCount})` },
          ]}
          value={show}
          onChange={(key) =>
            setParams((previous) => {
              const next = new URLSearchParams(previous);
              next.set('show', key);
              return next;
            })
          }
        />
        <SearchInput
          id="org-events-search"
          label="Search events"
          className="w-72"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          placeholder="Search events…"
        />
      </div>

      {workspaces.isPending ? (
        <SkeletonTable rows={4} columns={['w-[30%]', 'w-[15%]', 'w-[20%]', 'w-[10%]', 'w-[15%]']} label="Loading events…" />
      ) : workspaces.isError ? (
        <ErrorNotice title="Events unavailable" message={errorMessage(workspaces.error)} />
      ) : rows.length === 0 && needle.length > 0 ? (
        <Panel>
          <EmptyState icon={<CalendarDaysIcon aria-hidden className="size-6" />} title="No matches" message={`No event matches “${search.trim()}”.`} />
        </Panel>
      ) : rows.length === 0 ? (
        <Panel>
          <EmptyState
            icon={<CalendarDaysIcon aria-hidden className="size-6" />}
            title={show === SHOW.past ? 'Nothing past yet' : 'Nothing coming up'}
            message={
              show === SHOW.past
                ? 'Delivered events move here once their date has gone by.'
                : 'A request becomes an event here once it has come through the inquiry stage and been approved.'
            }
          />
        </Panel>
      ) : (
        <DataTable
          headers={[
            { label: 'Event', sortKey: SORT_FIELDS.event },
            { label: 'Date', sortKey: SORT_FIELDS.date },
            { label: 'Location', sortKey: SORT_FIELDS.location },
            { label: 'Participants', sortKey: SORT_FIELDS.participants },
            { label: 'Stage', sortKey: SORT_FIELDS.stage },
          ]}
          rightAlignedHeaders={[3]}
          sort={sort}
          onSort={onSort}
        >
          {rows.map((row) => (
            <tr key={row.id} className="border-t border-portal-line align-middle">
              <td className="p-3 text-portal-small">
                {row.eventId ? (
                  <Link to={`/events/${row.eventId}`} className="font-bold text-primary underline-offset-2 hover:underline">
                    {row.programName ?? 'Event'}
                  </Link>
                ) : (
                  <span className="font-bold">{row.programName ?? 'Event'}</span>
                )}
                {row.reference ? <span className="block text-portal-micro text-portal-muted">{row.reference}</span> : null}
              </td>
              <td className="p-3 text-portal-small text-portal-muted whitespace-nowrap">{shortDate(row.startAt)}</td>
              <td className="p-3 text-portal-small text-portal-muted">
                {[row.venueName, [row.city, row.state].filter(Boolean).join(', ')].filter(Boolean).join(' · ') || '—'}
              </td>
              <td className="p-3 text-portal-small text-right">{row.participants ?? '—'}</td>
              <td className="p-3 text-portal-small whitespace-nowrap">
                <Pill tone={row.stageTone}>{row.stageLabel}</Pill>
              </td>
            </tr>
          ))}
        </DataTable>
      )}
    </div>
  );
}
